export default {
  // Appended to a duplicate's name ("Concrete C25/30 (copy)"). Catalogue names are unique, so an
  // unchanged name would be refused on save.
  copy_suffix: "(copy)",
  close: "Close",
  loading: "Loading...",
  show_more: "Show more",
  show_less: "Show less",
  // The 404 page's tab title (`meta.title` of the NotFound route).
  not_found_page: "Page not found",
  // The material, machine and additional effort overviews.
  catalog_row: {
    headers: {
      changed: "Last changed",
    },
  },
  // The reports' pickers (staff, machines, materials, additional efforts): whoever may add to the
  // project also gets the customer's and the master data's entries below the project's own. Such an
  // entry is added to the project when the report is saved (docs/report-panels.md).
  report_assignment: {
    groups: {
      project: "On the project",
      customer: "On the customer",
      catalogue: {
        users: "Other employees",
        machines: "From master data",
        materials: "From master data",
        additionalEfforts: "From master data",
      },
    },
    // After the groups outside the project.
    assigns: "added to the project",
    more: "Not all matches shown. Please refine the search.",
    searching: "Searching…",
    new_tag: "New to project",
    dialog_title: "New to the project",
    dialog_text: {
      customer: "On the customer's list but not on the project yet. Added to it when you save.",
      catalogue: "Not on the project yet. Added to it when you save.",
    },
    customer_takeover: "The selling price is taken over for the customer as well.",
  },
  // Under the amount in the dialog of a material the report (the shift) already books at the same price:
  // the amount goes onto that line instead of a second one.
  booked_amount: {
    already: "{amount} already booked",
    combined: "{amount} already booked · {total} in total",
  },
  // The devices a report entry books (construction report and hourly-wage report): pick numbers or
  // type a new one.
  device_numbers: {
    label: "Device number",
    add: "New device number…",
    available: "Available",
    taken: "Device number {number} belongs to {machine}.",
    // Under the picked numbers of a machine the project has already, for the new ones among them: the
    // report save gives them to the project's machine.
    adds_to_project:
      "{numbers} is new and is added to the machine in the project on save. | {numbers} are new and are added to the machine in the project on save.",
  },
  // Purchase prices of a machine / material: the same item can cost different amounts depending on
  // where it was bought. One variant is the default.
  purchase_price_variants: {
    title: "Purchase prices",
    label: "Name (e.g. supplier)",
    // The name an unnamed price is shown with. Not "Default": the unnamed price need not be the default,
    // and that word belongs to the toggle that makes one.
    unnamed: "Unnamed",
    price: "Purchase price",
    currency: "Currency",
    default: "Default",
    add: "Add purchase price",
    remove: "Remove purchase price",
    missing_in_currency: "No price in {currency}. Bookings at this price are costed at 0.",
    also_in: "Also priced:",
    show_currency: "Show the price in {currency}",
    unique_label: "This name is already in use.",
    unique_unnamed: "Only one purchase price can stay unnamed.",
    select: "Purchase price",
    select_hint: "Which price was it bought at?",
    more_count: "+{count} more | +{count} more",
    count: "{count} purchase price | {count} purchase prices",
    range: "{min} – {max}",
    removed: "removed",
    live_price: "current price",
    live_price_hint: "This booking predates fixed prices and is costed at today's purchase price.",
    // On the machine or material, under one of its purchase prices: the projects costing at another one
    // ("5 projects: 505.00 EUR"), and in a dialog each of them with its price. Names and dates are never passed
    // as parameters: escapeParameter turned "C25/30" into "C25&#x2F;30".
    in_projects: {
      title: "Purchase prices in projects",
      count: "{count} project | {count} projects",
      // Preceded by the name of a price only projects have.
      only_in: "only in {count} project | only in {count} projects",
      not_in_record: "not in the master data",
      // Followed by the master data's price.
      record: "Master data:",
      record_no_price: "no price in {currency}",
      project: "Project",
      price: "Price",
      difference: "Difference",
      // Followed by the name the project gave the price.
      project_label: "in the project:",
      // Followed by the date.
      since: "since",
      hint_kept: "A project keeps its prices when they change here.",
      has_more: "Compared with the {count} most recent projects.",
    },
  },
  // The menu beside a selling price, on a customer and in a project. "At purchase price" is stored as a
  // surcharge of 0 %, "Free of charge" as a fixed price of 0 — free, not "not set".
  selling_price: {
    kind_menu: "Kind of selling price",
    kinds: {
      fixed: "Fixed price",
      variable: "Surcharge",
      purchase_price: "At purchase price",
      free: "Free of charge",
    },
    purchase_price_hint: "The selling price is the purchase price that is booked.",
    free_hint: "Free: the selling price is {amount}.",
  },
  // Units of machines and additional efforts (enum `MeasurementUnit`). Only the time and piece
  // units need a translation; every other unit shows its SI symbol from measurement_units.js.
  units: {
    short: {
      HOUR: "h",
      DAY: "day | days",
      PIECE: "pc | pcs",
      FLAT_RATE: "flat",
      SQUARE_METER: "m²",
      RUNNING_METER: "rm",
      WEEK: "wk | wks",
      MONTH: "mo | mos",
      SET: "set | sets",
    },
    long: {
      HOUR: "Hour",
      DAY: "Day",
      PIECE: "Piece",
      FLAT_RATE: "Flat rate",
      RUNNING_METER: "Running metre",
      WEEK: "Week",
      MONTH: "Month",
      SET: "Set",
    },
    legacy: "Former unit (free text)",
    unresolved: "Check unit",
    unresolved_hint: "Check unit: previously “{legacy}”",
  },
  importExport: {
    title: "Import / Export",
    export: "Export",
    import: "Import",
    exportDialog: {
      title: "Export",
      what: "Content",
      dataOption: "All data",
      templateOption: "Empty template",
      format: "Format",
      germanOption: "For Excel",
      technicalOption: "Standard CSV",
      submit: "Download",
    },
    dialog: {
      title: "Import data",
      fileLabel: "File",
      drop: "Drag a CSV file here or browse",
      existingLabel: "Existing data",
      skipOption: "Skip",
      updateOption: "Update",
      employeeMatchHint: "Matched by personnel number, falling back to first and last name.",
      noFile: "Please choose a CSV file first.",
      invalidType: "Please choose a CSV file (.csv).",
      submit: "Import",
    },
    result: {
      totalRows: "Total rows",
      created: "Created",
      updated: "Updated",
      skipped: "Skipped",
      failed: "Failed",
      errorsTitle: "Failed rows",
      row: "Row",
      field: "Field",
      message: "Message",
    },
  },
  map: {
    osm_attribution:
      "&copy; <a href='https://www.openstreetmap.org/copyright' target='_blank' rel='noopener'>OpenStreetMap</a> contributors",
    address_search: {
      // Shown in place of the search field when the address lookup is unreachable. The
      // provider is deliberately unnamed — what matters is that another way in exists.
      unavailable: "Address search is currently unavailable",
      retry: "Try again",
    },
  },
  // The address section (`LewAddressSection`) — the same field names for every screen
  // that captures an address, and for the change history of those fields.
  address: {
    search_placeholder: "Enter an address",
    edit_manually: "Edit address manually",
    street: "Street",
    house_number: "House Number",
    postcode: "Postcode",
    place: "Place",
    state: "State",
    country: "Country",
  },
  // A shift's weather course (`LewWeatherCourse`) under a shift's three weather readings in the
  // construction report; the day submission's row of hours uses the same wording. The weather states
  // themselves are in services/weather.js.
  weather_course: {
    // The strip with every hour, for screen readers.
    hourly: "Weather hour by hour",
    temperature: "{value} °C",
    temperature_range: "{min}–{max} °C",
    precipitation: "{amount} mm",
    precipitation_long: "{amount} mm precipitation",
    gusts: "Gusts up to {speed} km/h",
  },
  // The sections of the Protokoll tab (`LewEntityActivity`). Here rather than in a feature file
  // because the same tab hangs off eight record kinds and has to read the same on all of them.
  activity: {
    metadata: "Metadata",
    id: "ID",
    references: "References",
    references_empty: "Nothing references this record.",
    references_more: "and {count} more",
    // The two ways to the rest of a kind. The link goes to that kind's own overview, filtered to this
    // record; loading a page at a time is only offered where no such overview exists.
    references_show_all: "Show all",
    references_load_more: "Load more",
    // The banner on the filtered overview, naming the record being filtered on.
    filtered_by_reference: "Filtered: references {name}",
    filter_clear: "Clear filter",
    // Tooltip on the reference chips. They are the overview and the filter at once: the number says
    // how many there are, a click shows only those.
    filter_by: "Show only this kind",
    history: "Activity",
    // Date headings in the history. Only these two days get a name — for anything older the date
    // itself is the answer to "when was that".
    today: "Today",
    yesterday: "Yesterday",
    history_empty: "No change has been recorded for this record yet.",
    // A project's history also lists its catalogue, reports and daywork invoices; the chips above it
    // narrow it to some of them. This chip stands for the project itself.
    kind_project: "Project",
    history_empty_filtered: "No change has been recorded for this selection yet.",
    // Kept apart from the empty texts: a request that failed does not know whether there is nothing.
    load_failed: "Could not be loaded.",
  },
  // The field names of a record's facts. Without the colon: `LewEntityMeta` adds it itself, because
  // there the name and the value sit on one line — on the Protokoll tab they sit one above the other.
  meta: {
    created_at: "Created at",
    created_by: "Created by",
    last_modified_at: "Last modified at",
    last_modified_by: "Last modified by",
  },
  // Key labels in keyboard hints (LewKeyHints). macOS shows ⌘ instead.
  keys: {
    ctrl: "Ctrl",
  },
  // Archiving instead of deleting (docs/archiving.md): the detail pages' "Remove" and "Restore", their notice,
  // and the overviews' archive, opened from their ⋮.
  archive: {
    remove_title: "Remove?",
    remove_title_named: "Remove “{name}”?",
    remove_text:
      "The entry disappears from every overview and picker. If something already uses it, it stays there and moves to the archive, where you can restore it at any time. Otherwise it is deleted.",
    restore: "Restore",
    banner: "Archived on {date}",
    banner_by: "Archived on {date} by {name}",
    banner_hint: "No longer appears in any overview or picker.",
    badge: "Archived",
    // An overview's ⋮ and the archive view it opens.
    show: "Show archive",
    overview_hint: "They appear in no overview or picker. Open an entry to restore it.",
  },
  actions: {
    agree: "Agree",
    // Labels the actions button in an entity page header (`LewEntityPage`).
    actions: "Actions",
    submit: "Save",
    cancel: "Cancel",
    create: "Create",
    // The one button above a catalogue (material, machine, additional effort): its menu offers the
    // catalogue record and the packet, each with a label of its own.
    create_new: "Create new",
    apply: "Apply",
    // Generic verbs for icon-only controls that have no screen-specific wording of their own.
    // Anything with a domain noun attached ("Delete report") belongs in that screen's file.
    add: "Add",
    edit: "Edit",
    delete: "Delete",
    // Catalogue record pages (material, machine, additional effort): open a form, write nothing.
    duplicate: "Duplicate",
    create_packet: "Create packet from this",
    remove: "Remove",
    // The ⋮ beside an overview's create button.
    more: "More actions",
    open: "Open",
    download: "Download",
    restore: "Restore",
    expand: "Show details",
    collapse: "Hide details",
  },
  table: {
    loading_text: "Loading elements...",
    // The arrow at the end of a row: its label and tooltip. A right-click on the row does the same.
    expand_row: "Show details",
    collapse_row: "Hide details",
    right_click_hint: "{action} (or right-click the row)",
    // The pin at the end of a row: screen-reader label (`aria-pressed` says whether it is set) and mouse
    // tooltip, worded like the planning calendars'. Pinned rows stay on top for the signed-in user only.
    pin: "Pin",
    pin_hint: "Pin to the top of the list (just for you)",
    unpin_hint: "Unpin",
    no_data_text: "No data available",
    no_results_text: "No elements found",
    no_more_items: "No further data available",
    load_more: "load more",
    scroll_to_top: "Back to top",
    // Card lists (narrow viewports) have no column headers to click, so sorting moves into a menu.
    sort_by: "Sort",
    sort_ascending: "Ascending",
    sort_descending: "Descending",
    select_all: "Select all",
    clear_selection: "Clear selection",
    selected: "{count} selected",
    actions: {
      action: "Actions",
      delete: "delete",
      duplicate: "duplicate",
    },
  },
  modal: {
    close: "Close",
  },
  reference: {
    // Stands in for a reference (position, role, …) whose target no longer exists. The technical id
    // must never reach the UI — it means nothing to anyone reading it.
    unknown: "Unknown",
  },
  error: {
    internal_server_error: "Something went wrong.",
    // Fallbacks for rejected changes that carry no violation code (field validation, for instance).
    invalid_request: "The changes could not be saved. Please check your input.",
    forbidden: "You do not have permission for this action.",
    not_found: "The entry could not be found. It may have been deleted in the meantime.",
    payload_too_large: "The upload is too large to be accepted.",
    no_response_read: "The server did not answer. Please try again.",
    no_response_write:
      "The server did not answer, so it is unclear whether the change was saved. Please check before trying again.",
    // Only for failures reported to Sentry; the trace id joins them to the backend issue.
    reference: "Reference:",
    referenceCopy: "Copy reference",
    referenceCopied: "Reference copied",
    previous_redirect: "Click here to come back to the previous page.",
    dashboard_redirect: "Or Click here to come to the Dashboard.",
  },
  component: {
    combobox: {
      submitNewTag: 'Press <kbd>{key}</kbd> to create tag "<strong>{value}</strong>"',
    },
    file: {
      noDisplayPossible: "Data upload possible after first safe.",
      modal: {
        title: "Edit File",
      },
      table: {
        name: "Name",
        createdAt: "Created at",
        dueDate: "Valid until",
        tags: "Tags",
        deleteConfirm: {
          title: "Delete File?",
          text: 'Are you sure you want to delete "{name}"?',
          agree: "Delete",
        },
      },
      form: {
        tags: "All Tags",
        download: "Download",
        view: "View",
        createdAt: "Uploaded At",
        uploadedBy: "Uploaded By",
        updatedAt: "Updated At",
        updatedBy: "Updated By",
        documentDate: "Document date",
        documentTime: "Time",
        documentDateHelp:
          "The date the document itself carries; for a photo, when it was taken. Leave empty if it matches the upload; the upload time then applies.",
        dueDate: "Valid until",
        dueLeadDays: "Notice (days)",
        dueDateHelp:
          "The date the document stays valid until. After it the file counts as expired and is flagged in the list. Leave empty if the document never expires.",
        dueLeadDaysHelp:
          "How many days before the due date the warning starts. Empty: the workspace's notice period applies.",
      },
      due: {
        tooltip: {
          expired: "Expired {days} days ago",
          today: "Expires today",
          remaining: "Valid for another {days} days",
          unknown: "Valid until",
        },
      },
      pond: {
        maxFileExceeded: "The maximum number of files to upload is {value}",
        singleFile: "Please drop a single file.",
        maxSizeExceeded: "This file is too large. The maximum is {value}.",
        dropFilesHere: "Drop files here...",
        uploadFailed: "Upload failed",
        uploadNoAnswer: "No answer from the server",
      },
    },
    autocomplete: {
      noDataText: "Not found {value}",
      // While the first page of a paged picker list is on its way.
      searching: "Searching…",
    },
    // The end of a paged picker list (`LewLoadMore`) when the next page did not arrive.
    load_more: {
      retry: "Load more",
    },
    table: {
      noDataText: "No Data available",
    },
    formDialog: {
      close: "Close",
      add: "Add",
      save: "Save",
    },
    confirmationDialog: {
      confirm: "Confirm",
      cancel: "Cancel",
    },
    dateInput: {
      invalidFormat: "Invalid Date",
    },
  },
  update: {
    message: "A new version is available.",
    action: "Update",
  },
  // Gold strip above the app bar and tab title of a test, pre-release or development deployment
  // (APP_BANNER, services/deployment.ts).
  deployment: {
    development: "Development version",
    staging: "Pre-release version",
    test: "Test version",
    // At the front of the tab title, where every character costs room for the page's name.
    short: {
      development: "Dev",
      staging: "Staging",
      test: "Test",
    },
  },
  // "Save & new" and "Save & copy" beside a create page's Save, and the notice after it. See
  // docs/navigation.md.
  create_series: {
    save_and_new: "Save & new",
    save_and_copy: "Save & copy",
    more: "More save options",
    created: "“{name}” created",
  },
  // Unsaved changes: the line beside the save button, the question before leaving, and the offer to bring
  // back a draft kept in the browser. See docs/unsaved-changes.md.
  unsaved: {
    status: "Unsaved changes",
    dialog: {
      title: "Unsaved changes",
      text: "What was changed here has not been saved:",
      save_there: "To save, choose “Keep editing” and save there.",
      stay: "Keep editing",
      discard: "Discard",
      save: "Save",
    },
    draft: {
      found: "Unsaved changes from {when}",
      outdated: "Saved again since. What was not changed here stays current.",
      restore: "Restore",
      dismiss: "Discard",
    },
  },
  sentry: {
    formTitle: "Report a Bug",
    isRequiredLabel: "(required)",
    submitButtonLabel: "Send Bug Report",
    cancelButtonLabel: "Cancel",
    confirmButtonLabel: "Confirm",
    addScreenshotButtonLabel: "Add a screenshot",
    removeScreenshotButtonLabel: "Remove screenshot",
    messageLabel: "Description",
    messagePlaceholder:
      "What were you trying to do, and what happened instead? A screenshot helps us understand the problem.",
    successMessageText: "Thank you for your report!",
    sent: {
      sending: "Sending bug report …",
      title: "Bug report received",
      text: "Thank you! Our team has received your report and will look into it as soon as possible.",
      reply: "If we have any questions, we will get back to you by email.",
      reference: "Reference {reference}",
    },
    // `{'@'}`: a bare @ starts a linked message in vue-i18n.
    errorGenericText:
      "Your report could not be sent, perhaps because of the internet connection or an ad blocker. Please try again or write to support{'@'}lewara.de.",
    errorTimeoutText:
      "We could not tell whether your report arrived. Please send it again or write to support{'@'}lewara.de.",
    errorForbiddenText: "Your report could not be sent. Please write to support{'@'}lewara.de.",
    highlightToolText: "Highlight",
    hideToolText: "Hide",
    removeHighlightText: "Remove",
    priorityLabel: "Priority",
    priorities: {
      low: "Low",
      normal: "Normal",
      high: "High",
      critical: "Critical",
    },
    attachments: {
      attach: "Attach files",
      remove: "Remove attachment",
      tooLarge: "“{name}” is too large: at most {max}\u00a0MB for all attachments together.",
      unreadable: "“{name}” could not be read.",
    },
  },
}
